import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { DropdownProps, Form, TextAreaProps } from 'semantic-ui-react';
import { z } from 'zod';
import HoneypotField from '../../../../components/form/honeypotField';
import useBotCheck from '../../../../hooks/useBotCheck';
import MailService from '../../../../service/mails';
import Alert from '../../../../utils/alert';
import { APPROACH_OPTIONS, PROJECT_NEEDS } from '../../inform3d.content';
import './projectForm.css';

const REQUEST_FAILED_MESSAGE =
    'We are sorry but we were not able to process your request. Please try again or send us a direct email.';

const formSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    email: z.string().email('Invalid email address'),
    company: z.string().optional(),
    needs: z.array(z.string()).min(1, 'Please choose at least one'),
    // approaches: z.array(z.string()),
    details: z.string().optional(),
    // Honeypot. Left unvalidated on purpose: a non-empty value must fail silently
    // rather than surface a validation error. See useBotCheck.
    website: z.string().optional(),
});

type FormSchemaType = z.infer<typeof formSchema>;

const toOptions = (values: string[]) => values.map((value) => ({ key: value, text: value, value }));

const NEED_OPTIONS = toOptions(PROJECT_NEEDS);
const APPROACH_DROPDOWN_OPTIONS = toOptions(APPROACH_OPTIONS);

function buildDescription(data: FormSchemaType) {
    return [
        `Company: ${data.company || 'Not specified'}`,
        `Needs: ${data.needs.join(', ')}`,
        // `Approaches: ${data.approaches.length ? data.approaches.join(', ') : 'No preference'}`,
        `Details: ${data.details || 'Not specified'}`,
    ].join('\n');
}

function ProjectForm() {
    const { interactionProps, isBotSubmission, recordSubmission } = useBotCheck({
        storageKey: 'inform3d.lastSubmittedAt',
    });
    const [isSubmitted, setIsSubmitted] = useState(false);

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
    } = useForm<FormSchemaType>({
        defaultValues: {
            name: '',
            email: '',
            company: '',
            needs: [],
            // approaches: [],
            details: '',
            website: '',
        },
        resolver: zodResolver(formSchema),
    });

    const onSubmit = handleSubmit(async (data) => {
        // `details` is left out of the link scan on purpose: a visitor describing a
        // project may legitimately paste a URL there.
        if (isBotSubmission({
            honeypot: data.website,
            textFields: [data.name, data.company],
        })) {
            // Show the same confirmation a visitor sees, but send nothing. A bot that
            // is told it was rejected only teaches its operator what to change.
            setIsSubmitted(true);
            reset();
            return;
        }

        const wasSent = await new Promise<boolean>((resolve) =>
            MailService.sendUserRequest(
                {
                    clientEmail: data.email,
                    clientName: data.name,
                    description: buildDescription(data),
                },
                () => resolve(true),
                (error: { msg?: string }) => {
                    Alert.error(error?.msg || REQUEST_FAILED_MESSAGE);
                    resolve(false);
                }
            )
        );

        if (!wasSent) {
            return;
        }

        recordSubmission();
        setIsSubmitted(true);
        reset();
    });

    return (
        <div className="inform__form-panel">
            <div className="inform__form-intro">
                <h2>Send a Project Request</h2>
                <p>
                    Tell us what you have and what you need out of it. We reply by email, usually within one
                    working day.
                </p>
            </div>

            <Form onSubmit={onSubmit} {...interactionProps}>
                <div className="inform__field-row">
                    <Controller
                        name="name"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <Form.Input
                                disabled={isSubmitting}
                                className="sp-form-input required"
                                id="inform-name"
                                name="name"
                                label="First and Last Name"
                                placeholder="Jane Doe"
                                value={value}
                                onChange={(_, data) => onChange(data.value)}
                                error={errors.name?.message}
                            />
                        )}
                    />

                    <Controller
                        name="email"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <Form.Input
                                disabled={isSubmitting}
                                className="sp-form-input required"
                                id="inform-email"
                                name="email"
                                type="email"
                                label="Email"
                                placeholder="jane.doe@example.com"
                                value={value}
                                onChange={(_, data) => onChange(data.value)}
                                error={errors.email?.message}
                            />
                        )}
                    />
                </div>

                <Controller
                    name="company"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <Form.Input
                            disabled={isSubmitting}
                            className="sp-form-input"
                            id="inform-company"
                            name="company"
                            type="text"
                            label="Company or Organization"
                            placeholder="Optional"
                            value={value}
                            onChange={(_, data) => onChange(data.value)}
                            error={errors.company?.message}
                        />
                    )}
                />

                <Controller
                    name="needs"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <Form.Select
                            multiple
                            disabled={isSubmitting}
                            className="sp-form-input required"
                            id="inform-needs"
                            name="needs"
                            label="What do you need?"
                            placeholder="Select all that apply"
                            options={NEED_OPTIONS}
                            value={value}
                            onChange={(_, data: DropdownProps) => onChange(data.value as string[])}
                            error={errors.needs?.message}
                        />
                    )}
                />

                {/* <Controller
                    name="approaches"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <Form.Select
                            multiple
                            disabled={isSubmitting}
                            className="sp-form-input"
                            id="inform-approaches"
                            name="approaches"
                            label="Which approaches interest you?"
                            placeholder="Select any that apply - or none, and we will advise"
                            options={APPROACH_DROPDOWN_OPTIONS}
                            value={value}
                            onChange={(_, data: DropdownProps) => onChange(data.value as string[])}
                            error={errors.approaches?.message}
                        />
                    )}
                /> */}

                <Controller
                    name="details"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <Form.TextArea
                            disabled={isSubmitting}
                            className="sp-form-input"
                            id="inform-details"
                            name="details"
                            label="Project details"
                            // placeholder="What are the documents, what should the output look like, and when do you need it?"
                            rows={4}
                            value={value}
                            onChange={(_, data: TextAreaProps) => onChange(data.value)}
                            error={errors.details?.message}
                        />
                    )}
                />

                <Controller
                    name="website"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <HoneypotField value={value ?? ''} onChange={onChange} />
                    )}
                />

                <div className="sp-form-actions">
                    <button type="submit" className="sp-btn sp-btn--primary sp-btn--lg" disabled={isSubmitting}>
                        Send Project Brief
                    </button>
                </div>
            </Form>

            {/*
                The overlay clips the sliding confirmation panel. The panel itself cannot
                live behind `overflow: hidden` on .inform__form-panel, because that would
                also clip the Semantic dropdown menus.
            */}
            <div className="inform__form-overlay">
                <div
                    className={`inform__confirmation ${
                        isSubmitted ? 'inform__confirmation--visible' : 'inform__confirmation--hidden'
                    }`}
                >
                    <div className="sp-section-header">
                        <h2>Thank you - your project request is with us</h2>
                        <p className="sp-section-description">
                           We will review your request and get back to you as soon as possible.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProjectForm;
